import { isValidThemeHex } from '@dutchie/common/utils/concierge-luminance';

import { normalizeThemeColor } from './concierge-color-input';
import { ConciergeCustomizationSettings } from './concierge-theme';
import type { ConciergeTokenPair } from './read-concierge-theme-tokens';

export type ConciergeSettingsSource = 'admin' | 'partner' | 'stock';

export type ConciergeSettingsResolution = {
  settings: ConciergeCustomizationSettings | null | undefined;
  source: ConciergeSettingsSource;
  rejections: string[];
};

type PairVerdict = { rejection: string } | { settings: ConciergeCustomizationSettings };

// One gate, used by every tier. The authored strings never reach a stylesheet —
// only the re-serialized hex does, which preserves the injection boundary that
// makes buildConciergeOverridesCss safe to interpolate.
function validatePair(canvas: unknown, accent: unknown): PairVerdict {
  const canvasColorHex = normalizeThemeColor(canvas);
  const accentColorHex = normalizeThemeColor(accent);

  if (!isValidThemeHex(canvasColorHex) || !isValidThemeHex(accentColorHex)) {
    return { rejection: 'canvas and accent must both be a 6-digit hex or rgb() value without alpha' };
  }

  return { settings: { accentColorHex, canvasColorHex } };
}

/**
 * Three-tier ladder: the partner's declared tokens, then Admin, then stock — a
 * declaration naming the concierge specifically outranks a per-store Admin setting.
 *
 * Validation is identical at every tier and covers format only. Contrast is not
 * judged here: a low-contrast pair is a supported choice, the admin warns about
 * it, and resolveConciergeTheme picks a readable ink. Do not reintroduce a
 * contrast gate or a per-tier exception — rejecting here would silently drop a
 * saved theme to stock with no signal to the retailer.
 * Rejection messages never echo the authored string: it can be arbitrary CSS of
 * unbounded length, and this flows to Datadog.
 */
export function resolveConciergeSettings(
  tokens: ConciergeTokenPair | null,
  adminSettings: ConciergeCustomizationSettings | null | undefined
): ConciergeSettingsResolution {
  const rejections: string[] = [];

  if (tokens) {
    const verdict = validatePair(tokens.canvas, tokens.accent);
    if ('rejection' in verdict) {
      rejections.push(`partner: ${verdict.rejection}`);
    } else {
      return { settings: verdict.settings, source: 'partner', rejections };
    }
  }

  const adminVerdict = validatePair(adminSettings?.canvasColorHex, adminSettings?.accentColorHex);
  if ('settings' in adminVerdict) {
    // Returned by reference, never copied, so ConciergeProviders' useMemo does
    // not re-inject the global stylesheet on every render.
    return { settings: adminSettings, source: 'admin', rejections };
  }

  // Stock: hand back undefined rather than the unusable admin object, so the
  // source label and the payload agree.
  return { settings: undefined, source: 'stock', rejections };
}
